import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCommentDock } from '../../context/CommentDockContext';
import { useNotifications } from '../../context/NotificationsContext';
import CommentThreadWindow from './CommentThreadWindow';
import { extractCommentText, getCommentAuthor } from '../../utils/commentText';
import '../../Css/comment-dock.css';

const PULSE_MS = 1900;

/** "daniel.reyes@gigaflightinc.com" -> "Daniel" */
const firstName = (value) => {
    const text = String(value || '').trim();
    if (!text) return '';
    const base = text.includes('@') ? text.split('@')[0] : text;
    const first = base.split(/[.\s_]/)[0] || base;
    return first.charAt(0).toUpperCase() + first.slice(1);
};

/** Newest comment on a chip: the latest unread notification, else the newest loaded comment. */
const chipPreview = (thread, unreadList, me) => {
    const latestUnread = unreadList[0];
    if (latestUnread) {
        const who = firstName(latestUnread.mentionedBy);
        const text = extractCommentText(latestUnread.comment);
        return who ? `${who}: ${text}` : text;
    }
    const latest = (thread.comments || [])[0];
    if (latest) {
        const author = getCommentAuthor(latest);
        const who = author && me && author.toLowerCase() === me ? 'You' : firstName(author);
        const text = extractCommentText(latest);
        return who ? `${who}: ${text}` : text;
    }
    return '';
};

const CommentDockHost = () => {
    const dock = useCommentDock();
    const notifications = useNotifications();
    const [pulsing, setPulsing] = useState({});
    const seenRef = useRef({});

    const threads = dock?.threads || [];
    const minimized = threads.filter((t) => t.minimized);
    const unreadByKey = useMemo(() => {
        const map = {};
        minimized.forEach((t) => {
            map[t.key] = notifications ? notifications.unreadForThread(t) : [];
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threads, notifications]);
    const countSignature = minimized.map((t) => `${t.key}=${unreadByKey[t.key]?.length || 0}`).join('|');

    // A chat pulses when its unread count goes up, so a new comment is noticed.
    useEffect(() => {
        const next = {};
        let any = false;
        const counts = {};
        minimized.forEach((t) => {
            const count = unreadByKey[t.key]?.length || 0;
            counts[t.key] = count;
            if (count > (seenRef.current[t.key] || 0)) {
                next[t.key] = true;
                any = true;
            }
        });
        seenRef.current = counts;
        if (!any) return undefined;
        setPulsing(next);
        const timer = setTimeout(() => setPulsing({}), PULSE_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countSignature]);

    if (!dock || typeof document === 'undefined' || !threads.length) return null;

    const { restoreThread, closeThread } = dock;
    const expanded = threads.filter((t) => !t.minimized);
    const me = (localStorage.getItem('userEmail') || '').trim().toLowerCase();

    const openChat = (thread) => {
        if (notifications) {
            notifications.openOrderChat({
                key: thread.key,
                orderNumber: thread.orderNumber,
                orderLineId: thread.orderLineId,
                quoteId: thread.quoteId,
                poLineId: thread.poLineId,
            });
        } else {
            restoreThread(thread.key);
        }
    };

    const dismissChat = (thread) => {
        if (notifications) notifications.dismissThread(thread);
        else closeThread(thread.key);
    };

    return createPortal(
        <>
            {expanded.map((thread, stackIndex) => (
                <CommentThreadWindow
                    key={thread.key}
                    thread={thread}
                    stackIndex={stackIndex}
                />
            ))}
            {minimized.length > 0 && (
                <div
                    className="comment-dock-tray"
                    role="region"
                    aria-label="Minimized comment chats"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="comment-dock-tray-header">Comments</div>
                    <div className="comment-dock-tray-list">
                        {minimized.map((thread) => {
                            const unreadList = unreadByKey[thread.key] || [];
                            const unread = unreadList.length;
                            const title = thread.subtitle ? `${thread.title} – ${thread.subtitle}` : thread.title;
                            const preview = chipPreview(thread, unreadList, me);
                            const label = `Open comments for ${title}${unread ? `, ${unread} unread` : ''}${preview ? `. ${preview}` : ''}`;
                            const dismissLabel = unread
                                ? `Dismiss and mark ${unread} comment${unread === 1 ? '' : 's'} as read`
                                : 'Dismiss';
                            return (
                                <div
                                    key={thread.key}
                                    className={`comment-dock-tray-chip${unread ? ' has-unread' : ''}${pulsing[thread.key] ? ' is-new' : ''}`}
                                >
                                    <button
                                        type="button"
                                        className="comment-dock-tray-item"
                                        title={label}
                                        aria-label={label}
                                        onClick={() => openChat(thread)}
                                    >
                                        <span className="comment-dock-tray-item-text">
                                            <span className="comment-dock-tray-item-title">{title}</span>
                                            {preview ? (
                                                <span className="comment-dock-tray-item-sub">{preview}</span>
                                            ) : null}
                                        </span>
                                        {unread > 0 && (
                                            <span className="comment-dock-tray-item-badge" aria-hidden="true">
                                                {unread > 99 ? '99+' : unread}
                                            </span>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="comment-dock-tray-dismiss"
                                        title={dismissLabel}
                                        aria-label={`${dismissLabel} (${thread.title})`}
                                        onClick={() => dismissChat(thread)}
                                    >
                                        ×
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </>,
        document.body
    );
};

export default CommentDockHost;
